import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Gavel, Loader2, Trash2, Upload } from 'lucide-react';
import { WeeklyDigestButton } from './WeeklyDigestButton';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';

const CHUNK = 4 * 1024 * 1024;
const SCOPE = { public: 'público', private: 'privado (solo tú)', firma: 'compartido con tu notaría' };

// Fallos notariales y registrales: daily public ingestion + PDFs you upload (private; shared with your firm on Premium)
export const NotarialRulings = () => {
  const { api, user } = useAuth();
  const [d, setD] = useState(null);
  const [topic, setTopic] = useState('');
  const [pct, setPct] = useState(null);
  const ref = useRef(null);
  const load = () => api.get('/doctrine/rulings', { params: { topic: topic || undefined } }).then((r) => setD(r.data)).catch(() => setD({ rulings: [], topics: {} }));
  useEffect(() => { load(); }, [topic]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (file) => {
    if (!file) return;
    const id = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i += 1) {
        await api.post(`/doctrine/upload/${id}/chunk`, file.slice(i * CHUNK, (i + 1) * CHUNK), { params: { index: i }, headers: { 'Content-Type': 'application/octet-stream' } });
        setPct(Math.round(((i + 1) / total) * 60));
      }
      const r = await runAiJob(api, `/doctrine/rulings/upload/${id}/finish`, { filename: file.name, total }, { timeout: 240000 });
      toast.success(`Fallo agregado: ${r.case_number} (${SCOPE[r.scope]})`); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'No se pudo subir'); } finally { setPct(null); if (ref.current) ref.current.value = ''; }
  };
  const del = async (id) => { try { await api.delete(`/doctrine/rulings/${id}`); toast.success('Fallo eliminado'); load(); } catch { toast.error('No se pudo eliminar'); } };
  if (!d) return null;
  return (
    <section data-testid="notarial-rulings" className="mb-10 rounded-2xl border border-slate-700 bg-slate-900/40 p-6">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-bold"><Gavel className="h-5 w-5 text-amber-400" />Fallos sobre notarios y conservadores · {d.rulings.length}</h2>
      <p className="mb-3 text-xs text-slate-400">Se buscan cada día en la prensa jurídica pública (el buscador del Poder Judicial y Diario Constitucional bloquean la lectura automática, por eso hay pocos), y puedes subir tus fallos en PDF (privados; con Premium se comparten con tu notaría). Alimentan «Doctrina vs fallos» y la doctrina de cada causa.</p>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select data-testid="rulings-topic-select" value={topic} onChange={(e) => setTopic(e.target.value)} className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200">
          <option value="">Todos los temas</option>{Object.entries(d.topics).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input ref={ref} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="rulings-file" onChange={(e) => upload(e.target.files[0])} />
        <button data-testid="rulings-upload-btn" disabled={pct !== null} onClick={() => ref.current?.click()} className="flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-slate-950 disabled:opacity-60">
          {pct !== null ? <><Loader2 className="h-3 w-3 animate-spin" />{pct < 60 ? `${pct}%` : 'Fidem lee el fallo...'}</> : <><Upload className="h-3 w-3" />Subir fallo</>}
        </button>
        <WeeklyDigestButton />
      </div>
      {!d.rulings.length && <p data-testid="rulings-empty" className="text-xs text-slate-500">Aún no hay fallos en este tema.</p>}
      <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
        {d.rulings.map((r) => (
          <div key={r.id} data-testid={`ruling-${r.id}`} className="rounded-xl border border-slate-800 p-3 text-xs">
            <div className="flex items-start gap-2">
              <p className="flex-1 font-semibold text-white">{r.court_name || 'Tribunal s/i'} · {r.case_number}{r.ruling_date ? ` · ${r.ruling_date}` : ''}</p>
              <span className="text-[10px] text-slate-500">{SCOPE[r.scope] || 'público'}</span>
              {r.owner_id === user?.id && <button data-testid={`ruling-delete-${r.id}`} onClick={() => del(r.id)} aria-label="Eliminar" className="text-slate-500 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
            <p className="mt-1 text-slate-300">{r.ai_summary}</p>
            <p className="mt-1 text-[10px] text-slate-500">{(r.topics || []).map((t) => d.topics[t]).join(' · ')}{r.source_url && <> · <a href={r.source_url} target="_blank" rel="noreferrer" className="text-amber-300 hover:underline">fuente</a></>}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
